import React, { useRef, useState } from 'react'

import { ChevronDownIcon, NfcIcon } from 'lucide-react'

import { alpha, Box, Stack, Tooltip, useTheme } from '@mui/material'

import { Button } from '@repo/ui'
import { useScanRouting, type ScanRoutingTarget } from 'contexts'

import { ScanSourcePicker } from './ScanSourcePicker'
import { readScanSource, writeScanSource, type ScanSource } from './scanSource'

/**
 * THE SCAN TARGET SELECTOR — the one control that says where the next tap lands.
 *
 * It is not a modal opener and not a momentary action: it is a stateful mode toggle that aims an
 * ongoing scan session at one control. Clicking it while aimed un-aims it, which leaves nothing
 * targeted and therefore ends the session (see the lifetime note in `ScanRoutingContext`).
 *
 * WHY IT IS SHARED. The toolbar (`ActionLine`) and every filter field (`SearchFilter`) render the
 * same control by design — same glyph, same states, same wording pattern — because an operator
 * has to recognise it as ONE thing wherever it appears. It was previously hand-rolled twice, and
 * the two copies had already drifted: the toolbar's tooltip named three states, the field's named
 * two, so the same button said different things about the same session depending on where it was
 * drawn. Per working-conventions §36, the second hand-roll becomes a shared component. Now the
 * state machine below exists once, and a third state could not be added to one surface only.
 *
 * THREE STATES, NOT TWO (2026-08-11). Purple has to mean "scans will land here", and that takes
 * two facts, not one: this control is aimed AND a scanner can actually deliver. They come apart
 * routinely — the pairing modal is dismissed without pairing, or the QR is never answered — and
 * the old two-state control rendered that case identically to a working one, so it promised a
 * landing that could never happen.
 *
 *  1. NOT AIMED — the rest treatment (neutral fill, neutral glyph). Unchanged.
 *  2. AIMED, NOT READY — a LIGHT brand tint, solid brand outline, brand glyph. Reads as the
 *     selected target but withholds the full strength that means ready.
 *  3. AIMED AND READY — the full brand state (house selected tint + stronger brand border).
 *
 * TINT STRENGTH separates 2 from 3 — roughly half, on the border as well as the fill — so they
 * read as one control getting more committed rather than as two unrelated looks. The two alphas
 * resolve to different luminance, so the distinction survives a greyscale screenshot without
 * depending on hue.
 *
 * 2 previously had NO fill and a DASHED border. The dashes read as a broken or disabled control
 * rather than one waiting on a scanner; the missing fill made it the one selected control in the
 * app not carrying the purple tint that means "selected" everywhere else.
 *
 * `aria-pressed` tracks AIMED, not readiness, because pressed is the toggle's own semantic state
 * and this button is aimed in both 2 and 3. Readiness is carried by the label instead, which is
 * why the label and the tooltip are ONE string here rather than two that can disagree.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * WHERE THE SCAN COMES FROM — the source decision, made by the operator (2026-08-19).
 *
 * This control used to decide the source implicitly, inside `ScanRoutingContext.aim`: a connected
 * desk reader claimed the session silently, and with no reader a modal opened offering "connect a
 * reader" as a PEER of the QR. Neither branch ever asked. An operator whose tags were across the
 * room could not choose their phone; an operator with no reader was shown hardware they may not
 * own, ranked alongside the thing they could actually do.
 *
 * FOUR STATES, and only ONE of them opens the picker:
 *
 *  | state                                       | click                                   |
 *  |---------------------------------------------|-----------------------------------------|
 *  | aimed (this control)                        | stop scanning — UNCHANGED               |
 *  | not aimed, a scanner is already delivering  | aim, instantly — UNCHANGED              |
 *  | not aimed, no scanner, source remembered    | aim + start from it, instantly          |
 *  | not aimed, no scanner, source unknown       | OPEN THE PICKER                         |
 *
 * The picker is therefore asked at most ONCE, and never for a scan — the source decision only
 * arises when nothing is delivering yet, which is once per session, not once per tap and not once
 * per field. Moving the target mid-run stays a single click, which is what it has to stay: an
 * operator walking a rail re-aims far more often than they start a session.
 *
 * "Remembered" is checked against what can deliver RIGHT NOW, never taken on trust: a remembered
 * reader on a machine with no reader is not usable, so the picker opens instead of committing to
 * a scanner that cannot answer (see `scanSource.ts` for why that check lives at the call site).
 * A remembered phone is always usable — the QR path is the pairing step, not a precondition.
 *
 * CHANGING IT AFTERWARDS is the corner chevron, and it exists because the fast path above would
 * otherwise be a one-way door: an operator who picked the reader on Monday has no way to say "I
 * am walking away from my desk" on Tuesday, since every plain click would keep committing to the
 * reader. It is revealed on hover/focus rather than drawn at rest because this control renders on
 * the toolbar AND on every filter field — a permanent second glyph on each of them would be a lot
 * of chrome for a decision made once a session — and it stays visible while the control is aimed,
 * so the LIVE source always has a visible handle. It is a SIBLING of the button, never nested
 * inside it: a button inside a button is invalid markup and double-fires every click.
 */

export interface ScanTargetButtonProps {
  /** Where scans go while this control is lit. Identity is `kind` + `key`. */
  target: ScanRoutingTarget
  /**
   * How the destination is NAMED in the copy, as it should read mid-sentence — "search",
   * "the Product filter". Separate from `target.label` (which names the destination in a
   * scan-landed toast) so each sentence can read naturally on its own surface.
   */
  destination: string
  /**
   * Defaults to `large` because this button always sits beside a search field, and that
   * field is 49px in BOTH places it appears — the list toolbar and each field row in the
   * filters popover. Overridable only for a row that isn't 49px; there isn't one today.
   */
  size?: 'small' | 'medium' | 'large'
}

export const ScanTargetButton = ({ target, destination, size = 'large' }: ScanTargetButtonProps) => {
  const theme = useTheme()
  const scanning = useScanRouting()

  // Anchors the picker to the whole control (button + chevron) so the panel lines up with what
  // opened it regardless of which of the two was clicked.
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Aimed = this exact control holds the app's single target slot. Compare `kind` as well as
  // `key`: a filter column and the toolbar search are different targets even if their keys ever
  // collide (see `ScanRoutingTargetKind`).
  const aimed = scanning.target?.kind === target.kind && scanning.target?.key === target.key
  // Ready is deliberately NOT `sessionActive` — a live session says a session exists, not that
  // anything is attached to it. See `scannerReady` in `ScanRoutingContext`.
  const ready = aimed && scanning.scannerReady

  /**
   * The remembered source, and whether it can deliver right now.
   *
   * Read on every render rather than held in state on purpose: the store is per-device and can be
   * written by ANY instance of this control (the toolbar's and each filter field's are separate
   * mounts), so a cached copy would let one of them keep acting on a preference another has
   * already replaced. It is a synchronous localStorage read of one short string.
   *
   * A reader is usable only while it is actually connected. A phone is always usable — pairing IS
   * the path, not a precondition — which is why the two arms are not symmetric.
   */
  const remembered = readScanSource()
  const rememberedUsable =
    remembered === 'reader' ? scanning.readerConnected : remembered === 'phone'

  /** What a plain click on the glyph would do, in the order the click handler tries them. */
  const plainClickOpensPicker = !aimed && !scanning.scannerReady && !rememberedUsable

  const handleClick = (e: React.MouseEvent) => {
    // Stopped here because a field's scan button sits inside a row whose own click opens the
    // options popover; aiming must not also open it.
    e.stopPropagation()
    if (plainClickOpensPicker) {
      setPickerOpen(true)
      return
    }
    // Un-aiming, or moving the target inside a live session — neither is a source decision, so
    // neither states a source. `aim` ends the session on the former and routes on the latter.
    if (aimed || scanning.scannerReady) {
      scanning.aim(target)
      return
    }
    // The fast path: a remembered, usable source starts scanning on this one click.
    scanning.aim(target, remembered as ScanSource)
  }

  const handlePick = (source: ScanSource) => {
    // Written only from an explicit pick — never inferred from what is plugged in, which is the
    // behaviour being fixed.
    writeScanSource(source)
    // Aimed already? Then this is a source change, and `aim` must not be used: aiming the
    // already-aimed target is the un-aim gesture, so it would END the session the operator is
    // trying to switch scanners on.
    if (aimed) scanning.chooseSource(source)
    else scanning.aim(target, source)
  }

  /**
   * The chevron is shown when there is something to change: while this control is AIMED (exactly
   * one such control exists, so exactly one chevron is drawn at rest), or while the picker it
   * opens is up. Otherwise it is revealed by hover/`:focus-visible` on the control — which is
   * precisely when the operator is about to click, and it keeps a second glyph off every filter
   * field in the drawer at rest.
   *
   * It is pointless where a plain click ALREADY opens the picker, so it is suppressed there
   * rather than offering two controls for one action.
   */
  const showSourceChevron = !plainClickOpensPicker

  /**
   * ONE sentence for the tooltip AND the screen reader. They were separate strings on the toolbar
   * copy and had already diverged (the tooltip said "connect a scanner" while the label said
   * "send scans to search"), which describes a different button to a screen-reader user than to a
   * sighted one. One string cannot drift from itself.
   */
  /**
   * WHICH SCANNER IS ACTUALLY DELIVERING, named rather than left to be deduced.
   *
   * The lit control has always said WHERE scans land and never WHAT they come from, which is the
   * half an operator cannot see: a reader silently claiming the session looked exactly like a
   * paired phone. Both can be attached at once — they are concurrent inputs to one session, not a
   * choice — so this reports both when both are live.
   */
  const liveSource =
    scanning.readerConnected && scanning.phoneConnected
      ? 'your phone or desk reader'
      : scanning.readerConnected
        ? 'your desk reader'
        : scanning.phoneConnected
          ? 'your phone'
          : null

  const rememberedPhrase = remembered === 'reader' ? 'your desk reader' : 'your phone'

  const label = aimed
    ? ready
      ? liveSource
        ? `Scans land in ${destination}, from ${liveSource}. Click to stop scanning.`
        : `Scans land in ${destination}. Click to stop scanning.`
      : `Aimed at ${destination}. Waiting for a scanner to connect.`
    : scanning.scannerReady
      ? scanning.target
        ? // Something ELSE holds the single target slot. "instead" is the whole sentence's job
          // here: it says a click MOVES the target rather than adding a second one.
          `Send scans to ${destination} instead`
        : // IDLE — a live session with a scanner attached and nothing aimed at all. A real
          // resting state since the filters drawer began releasing its target on close
          // (2026-08-15), and the state that manufactures "I scanned and nothing happened", so
          // it is named rather than left as the absence of a lit control.
          `Scanning is on, but nothing is receiving it. Click to send scans to ${destination}.`
      : rememberedUsable
        ? // The fast path. It says which scanner the click will start, because a click that
          // silently commits to a source is the whole defect being fixed — naming it up front is
          // what makes skipping the picker honest rather than another implicit decision.
          `Send scans to ${destination}, using ${rememberedPhrase}.`
        : // No usable source is remembered, so the click asks instead of assuming. This replaces
          // "Connect a scanner", which described the reader-less branch of a decision the
          // operator was never part of.
          `Choose a scanner, then send scans to ${destination}.`

  /**
   * BOTH aimed states carry a brand tint, because a selected control is purple-tinted everywhere
   * else in the app (the house values are brand @0.12 hover, @0.16 selected) and this one was the
   * exception at no fill.
   *
   * They stay apart by tint STRENGTH rather than tint-vs-nothing: waiting is roughly half of
   * ready, on the border as well as the fill, so the two read as one control getting more
   * committed rather than as two unrelated looks. That also keeps the difference out of hue
   * alone — the two alphas resolve to different luminance, so they survive a greyscale
   * screenshot, which is the property the old dashed border was there to protect.
   */
  const targetedSx = ready
    ? {
        color: theme.palette.brand[600],
        // The house "selected" tint.
        backgroundColor: alpha(theme.palette.brand[500], 0.16),
        borderColor: alpha(theme.palette.brand[500], 0.55),
        '&:hover': {
          color: theme.palette.brand[600],
          backgroundColor: alpha(theme.palette.brand[500], 0.22),
          borderColor: alpha(theme.palette.brand[500], 0.55),
        },
      }
    : {
        color: theme.palette.brand[600],
        // Chosen, but not yet able to receive a tap — so about half of ready on both axes. The
        // border used to be HEAVIER here (0.5) than in the ready state (0.4), which had the
        // progression backwards: the weaker state looked more committed than the stronger one.
        backgroundColor: alpha(theme.palette.brand[500], 0.07),
        borderColor: alpha(theme.palette.brand[500], 0.3),
        // Self-contained, because `@repo/ui`'s Button merges `sx` over its own styles with a
        // shallow spread — supplying `&:hover` REPLACES the variant's hover outright, so anything
        // this state needs on hover (holding the brand border colour) has to be restated here.
        '&:hover': {
          color: theme.palette.brand[600],
          backgroundColor: alpha(theme.palette.brand[500], 0.12),
          borderColor: alpha(theme.palette.brand[500], 0.3),
        },
      }

  return (
    <Box
      ref={anchorRef}
      sx={{
        position: 'relative',
        display: 'flex',
        // The chevron is revealed by hovering ANYWHERE on the control, not just by finding an
        // 18px corner — the corner is the target, the whole button is the trigger.
        '&:hover .scan-source-chevron': { opacity: 1 },
      }}
    >
      {/* SILENCED WHILE THE PICKER IS OPEN. The chevron sits INSIDE the button's bounds, so a real
          click on it leaves the pointer over the button — the tooltip then hangs over the panel
          that click just opened, covering its heading. An empty title renders no tooltip at all;
          the sentence is still on the button's `aria-label`, so nothing is lost to a screen
          reader. */}
      <Tooltip title={pickerOpen ? '' : label} enterDelay={600}>
        {/* Wrapped, because `@repo/ui`'s Button is not a forwardRef and MUI's Tooltip needs a ref
            on its child — the same wrap the toolbar's other tooltipped buttons use. */}
        <Stack>
        <Button
          variant="secondary"
          disableElevation
          // The old hardcoded `medium` claimed to match the action row, but that row is
          // 49px (search, bulk toggle, Views, Filter, Sort, Layout) and so is every search
          // field in the filters popover — leaving this the lone 40px control in both.
          size={size}
          // The on-state is announced, not just drawn: a control whose meaning is carried only by
          // a brand tint is invisible to a screen reader and to anyone who cannot separate the
          // two colours.
          aria-pressed={aimed}
          // Only when this click actually opens the picker. Announcing `aria-expanded` on a click
          // that aims or stops would promise a panel that never appears.
          aria-expanded={plainClickOpensPicker ? pickerOpen : undefined}
          aria-haspopup={plainClickOpensPicker ? 'dialog' : undefined}
          aria-label={label}
          // The session's lifetime is still decided in ONE place (`ScanRoutingContext.aim`),
          // never at a call site — clicking while lit un-aims the only lit control, which leaves
          // nothing targeted, and `aim` ends the session for exactly that reason. What this
          // handler adds is the SOURCE decision, which used to be made implicitly inside `aim`
          // by whatever happened to be plugged in. See the four-state table in the header.
          onClick={handleClick}
          sx={
            aimed
              ? targetedSx
              : {
                  '&:hover': { color: theme.palette.brand[600] },
                }
          }
        >
          {/* An NFC glyph, not a QR one: QR is one way to connect a scanner, not what the control
              does. A desk reader needs no QR at all. */}
          <NfcIcon strokeWidth={aimed ? 2 : 1} />
        </Button>
        </Stack>
      </Tooltip>

      {showSourceChevron && (
        <Tooltip title={pickerOpen ? '' : 'Change where scans come from'} enterDelay={600}>
          {/* A SIBLING of the button, not a child: a `<button>` inside a `<button>` is invalid
              markup and double-fires every click (the same trap `ScanSessionBlock`'s row-inside-
              row controls hit). Absolutely positioned so it costs no layout — the control stays
              exactly 49px in a toolbar that aligns to that height. */}
          <Box
            component="span"
            className="scan-source-chevron"
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
            aria-label="Change where scans come from"
            onClick={e => {
              // Twice over: the button beneath must not also aim, and a filter field's row must
              // not open its options popover.
              e.stopPropagation()
              setPickerOpen(true)
            }}
            onKeyDown={e => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              e.stopPropagation()
              setPickerOpen(true)
            }}
            sx={{
              position: 'absolute',
              right: 1,
              bottom: 1,
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              cursor: 'pointer',
              color: aimed ? theme.palette.brand[600] : 'text.secondary',
              // Drawn at rest only while this control is AIMED — there is exactly one of those on
              // screen, so the live source always has a visible handle and the other scan icons
              // stay a single clean glyph until hovered. `:focus-visible` is not optional here:
              // it is the only thing that makes the chevron reachable by keyboard at all.
              opacity: aimed || pickerOpen ? 1 : 0,
              transition: 'opacity 0.15s ease, background-color 0.15s ease',
              '&:hover': { backgroundColor: alpha(theme.palette.brand[500], 0.12) },
              '&:focus-visible': {
                opacity: 1,
                outline: `2px solid ${theme.palette.brand[600]}`,
                outlineOffset: 1,
              },
            }}
          >
            <ChevronDownIcon size={12} strokeWidth={2} />
          </Box>
        </Tooltip>
      )}

      <ScanSourcePicker
        anchorEl={pickerOpen ? anchorRef.current : null}
        onClose={() => setPickerOpen(false)}
        destination={destination}
        onPick={handlePick}
      />
    </Box>
  )
}

export default ScanTargetButton
